import { getValueByPath, isDifferent } from 'helpers/objects';
import { ELicenceHistoryActions } from 'pages/LegalEntitiesPage/helpers';

export enum EComparisonCategory {
  LicenceOwner = 'Licence Owner',
  LicenceConditions = 'Licence Conditions',
  SafeStorage = 'Safe Storage',
  Weapon = 'Weapon',
  Attachments = 'Attachments',
  AdditionalInformation = 'Additional Information',
  GeneralInfo = 'General Info',
}

export enum EDataType {
  prev = 'prev',
  post = 'post',
}

export enum EArrayType {
  Dictionary = 'dictionary',
  Simple = 'simple',
}

export enum EWeaponPropertyKey {
  FireCycleId = 'fireCycleId',
  Type = 'type',
  Make = 'make',
  Model = 'model',
  Calibre = 'calibre',
  Action = 'action',
  SerialNumber = 'serialNumber',
  StateUniqueId = 'stateUniqueId',
  OwnerName = 'ownerName',
  KeeperName = 'keeperName',
}

export enum EAttachmentPropertyKey {
  Filename = 'filename',
  DocumentTypeUniqueId = 'documentTypeUniqueId',
  Comment = 'comment',
}

export enum ESafeStoragePropertyKey {
  Address = 'address',
}

export interface IComparisonRow {
  beforeValue: any;
  afterValue: any;
  translationKey: string;
  titleSecondPart?: string;
  isDictionary?: boolean;
  dictionaryType?: string;
  isHtml?: boolean;
  isDate?: boolean;
  isArray?: boolean;
  arrayType?: EArrayType;
  isAddress?: boolean;
  countryDictionary?: any[];
  isWeaponList?: boolean;
  weaponPropertyKey?: string;
  isLegalEntityLink?: boolean;
  legalEntityIdKey?: string;
  isFirearmLink?: boolean;
  firearmIdKey?: string;
  isTranslatable?: boolean;
  weaponContext?: { before: any; after: any };
}

export interface IComparisonSection {
  categoryName: string;
  categoryTranslationKey: string;
  rows: IComparisonRow[];
  weaponCategory?: string;
  additionalRow?: {
    before?: number | string;
    after?: number | string;
    name: string;
  };
  weaponIndex?: number;
  isNew?: boolean;
  isRemoved?: boolean;
}

interface FieldConfig {
  path: string;
  translationKey: string;
  isDictionary?: boolean;
  dictionaryType?: string;
  isHtml?: boolean;
  isDate?: boolean;
  isArray?: boolean;
  arrayType?: EArrayType;
  isAddress?: boolean;
  countryDictionary?: any[];
  isWeaponList?: boolean;
  weaponPropertyKey?: string;
  isLegalEntityLink?: boolean;
  legalEntityIdKey?: string;
  isFirearmLink?: boolean;
  firearmIdKey?: string;
  isTranslatable?: boolean;
  transform?: (prev: any, post: any, current?: EDataType) => any;
  customComparison?: (prev: any, post: any) => boolean;
}

const compareFields = (previous: any, post: any, configs: FieldConfig[]): IComparisonRow[] => {
  const rows: IComparisonRow[] = [];

  configs.forEach((config) => {
    let beforeValue = getValueByPath(previous, config.path);
    let afterValue = getValueByPath(post, config.path);

    if (config.isArray) {
      beforeValue =
        beforeValue === null || beforeValue === undefined || beforeValue === ''
          ? []
          : beforeValue;
      afterValue =
        afterValue === null || afterValue === undefined || afterValue === '' ? [] : afterValue;
    }

    if (config.transform) {
      const before = beforeValue;
      beforeValue = config.transform(beforeValue, afterValue, EDataType.prev);
      afterValue = config.transform(before, afterValue, EDataType.post);
    }

    const hasChanged = config.customComparison
      ? config.customComparison(beforeValue, afterValue)
      : isDifferent(beforeValue, afterValue);

    if (hasChanged) {
      const row: IComparisonRow = {
        beforeValue,
        afterValue,
        translationKey: config.translationKey,
      };

      if (config.isDictionary) row.isDictionary = true;
      if (config.dictionaryType) row.dictionaryType = config.dictionaryType;
      if (config.isHtml) row.isHtml = true;
      if (config.isDate) row.isDate = true;
      if (config.isArray) row.isArray = true;
      if (config.arrayType) row.arrayType = config.arrayType;
      if (config.isAddress) row.isAddress = true;
      if (config.isLegalEntityLink) row.isLegalEntityLink = true;
      if (config.legalEntityIdKey) row.legalEntityIdKey = config.legalEntityIdKey;
      if (config.isFirearmLink) row.isFirearmLink = true;
      if (config.firearmIdKey) row.firearmIdKey = config.firearmIdKey;
      if (config.isTranslatable) row.isTranslatable = true;

      rows.push(row);
    }
  });

  return rows;
};

const FIELD_CONFIGS = {
  licenceOwner: [
    { path: 'owner.legalType', translationKey: 'legalEntityDetails.legalType.label' },
    {
      path: 'owner.displayUniqueId',
      translationKey: 'legalEntityDetails.displayUniqueId.label',
    },
    {
      path: 'owner.photoAttachmentUniqueId',
      translationKey: 'legalEntityDetails.photo.label',
      transform: (prev: any, post: any, current: EDataType) => {
        let label = 'general.no.label';
        if (current === EDataType.prev && prev) {
          label = 'licenceDetails.history.details.photo.label';
        } else if (current === EDataType.post && post) {
          label =
            !prev || post === prev
              ? 'licenceDetails.history.details.photo.label'
              : 'general.updated.label';
        }
        return label;
      },
      isTranslatable: true,
    },
    {
      path: 'owner.individual.prefixValueUniqueId',
      translationKey: 'legalEntityDetails.prefix.label',
      isDictionary: true,
      dictionaryType: 'civilianPrefix',
    },
    {
      path: 'owner.individual.firstName',
      translationKey: 'legalEntityDetails.firstName.label',
    },
    {
      path: 'owner.individual.middleNames',
      translationKey: 'legalEntityDetails.middleNames.label',
    },
    { path: 'owner.individual.lastName', translationKey: 'legalEntityDetails.lastName.label' },
    {
      path: 'owner.individual.dateOfBirth',
      translationKey: 'legalEntityDetails.dateOfBirth.label',
      isDate: true,
    },
    {
      path: 'owner.individual.placeOfBirth',
      translationKey: 'legalEntityDetails.placeOfBirth.label',
    },
    { path: 'owner.individual.height', translationKey: 'legalEntityDetails.height.label' },
    {
      path: 'owner.individual.occupation',
      translationKey: 'legalEntityDetails.occupation.label',
    },
    {
      path: 'owner.individual.genderUniqueId',
      translationKey: 'legalEntityDetails.gender.label',
      isDictionary: true,
      dictionaryType: 'gender',
    },
    {
      path: 'owner.individual.nationalityUniqueId',
      translationKey: 'legalEntityDetails.nationality.label',
      isDictionary: true,
      dictionaryType: 'nationality',
    },
    {
      path: 'owner.individual.identityDocumentNumber',
      translationKey: 'legalEntityDetails.identityDocumentNumber.label',
    },
    {
      path: 'owner.individual.identityDocumentTypeUniqueId',
      translationKey: 'legalEntityDetails.identityDocumentType.label',
      isDictionary: true,
      dictionaryType: 'identificationDocumentType',
    },
    {
      path: 'owner.primaryAddress',
      translationKey: 'legalEntityDetails.address.label',
      isAddress: true,
      customComparison: (prevAddress: any, postAddress: any) =>
        isDifferent(prevAddress?.line1, postAddress?.line1) ||
        isDifferent(prevAddress?.line2, postAddress?.line2) ||
        isDifferent(prevAddress?.city, postAddress?.city) ||
        isDifferent(prevAddress?.postalCode, postAddress?.postalCode) ||
        isDifferent(prevAddress?.countryUniqueId, postAddress?.countryUniqueId) ||
        isDifferent(prevAddress?.regionUniqueId, postAddress?.regionUniqueId),
    },
    {
      path: 'owner.organisation.organisationTypeUniqueIds',
      translationKey: 'legalEntity.licence.organisationType.label',
      isDictionary: true,
      isArray: true,
      arrayType: EArrayType.Dictionary,
      dictionaryType: 'organisationType',
    },
    {
      path: 'owner.organisation.departmentName',
      translationKey: 'legalEntity.licence.departmentName.label',
    },
    {
      path: 'owner.organisation.organisationCategoryUniqueId',
      translationKey: 'legalEntity.licence.organisationCategory.label',
      isDictionary: true,
      dictionaryType: 'organisationCategory',
    },
    {
      path: 'owner.organisation.organisationName',
      translationKey: 'licence.organisationName.label',
    },
  ] as FieldConfig[],

  licenceConditions: [
    {
      path: 'conditions.weaponNumberLimit',
      translationKey: 'legalEntity.licenceDetails.conditions.weaponNumLimit',
    },
    {
      path: 'conditions.weaponCategorisationUniqueId',
      translationKey: 'legalEntity.licenceDetails.conditions.weaponCat',
      isDictionary: true,
      dictionaryType: 'weaponLicenceCategory',
    },
    {
      path: 'conditions.competencyUniqueId',
      translationKey: 'legalEntity.licenceDetails.conditions.compentency',
      isDictionary: true,
      dictionaryType: 'weaponLicenceCompetency',
    },
    {
      path: 'conditions.carry',
      translationKey: 'legalEntity.licenceDetails.conditions.carryConditions',
      isDictionary: true,
      dictionaryType: 'weaponLicenceCondition',
    },
    {
      path: 'conditions.areaOfValidityUniqueId',
      translationKey: 'legalEntity.licenceDetails.conditions.areaOfValidity',
      isDictionary: true,
      dictionaryType: 'region',
    },
    {
      path: 'conditions.goodReason',
      translationKey: 'legalEntity.licenceDetails.conditions.goodReason',
    },
    {
      path: 'conditions.goodReasonUniqueIds',
      translationKey: 'legalEntity.licenceDetails.conditions.goodReason',
      isArray: true,
      arrayType: EArrayType.Dictionary,
      dictionaryType: 'weaponLicenceGoodReason',
    },
    {
      path: 'conditions.additionalConditions',
      translationKey: 'legalEntity.licenceDetails.conditions.additionalConditions',
      isHtml: true,
    },
  ] as FieldConfig[],

  safeStorage: [
    {
      path: 'conditions.hasSafeStorage',
      translationKey: 'legalEntity.licenceDetails.conditions.safeStorage',
      transform: (prev: any, post: any) =>
        prev !== post && post ? 'general.yes.label' : 'general.no.label',
      isTranslatable: true,
    },
    {
      path: 'conditions.safeStorageDescription',
      translationKey: 'legalEntity.licenceDetails.conditions.safeStorageDescription',
      isHtml: true,
    },
    {
      path: 'conditions.safeStorages',
      translationKey: 'legalEntity.licenceDetails.conditions.safeStorageList',
      transform: (prev: any, post: any, current: EDataType) => {
        const val = current === EDataType.prev ? prev : post;
        return val?.length || 0;
      },
    },
  ] as FieldConfig[],

  additionalInformation: [
    {
      path: 'additionalInformation',
      translationKey: 'legalEntity.licenceDetails.additionalInformation',
    },
  ] as FieldConfig[],

  generalInformation: [
    {
      path: 'general.paymentStatusUniqueId',
      translationKey: 'legalEntity.licenceDetails.generalInformation.paymentStatus',
      isDictionary: true,
      dictionaryType: 'weaponLicencePaymentStatus',
    },
  ] as FieldConfig[],
};

const WEAPON_PROPERTY_CONFIGS = [
  {
    key: EWeaponPropertyKey.FireCycleId,
    translationKey: 'legalEntity.licenceDetails.weapon.fireCycleId.column',
    isFirearmLink: true,
    firearmIdKey: 'firearmUniqueId',
  },
  {
    key: EWeaponPropertyKey.Type,
    translationKey: 'legalEntity.licenceDetails.weapon.type.column',
  },
  {
    key: EWeaponPropertyKey.Make,
    translationKey: 'legalEntity.licenceDetails.weapon.make.column',
  },
  {
    key: EWeaponPropertyKey.Model,
    translationKey: 'legalEntity.licenceDetails.weapon.model.column',
  },
  {
    key: EWeaponPropertyKey.Calibre,
    translationKey: 'legalEntity.licenceDetails.weapon.calibre.column',
  },
  {
    key: EWeaponPropertyKey.Action,
    translationKey: 'legalEntity.licenceDetails.weapon.action.column',
  },
  {
    key: EWeaponPropertyKey.SerialNumber,
    translationKey: 'legalEntity.licenceDetails.weapon.serialNumber.column',
  },
  {
    key: EWeaponPropertyKey.StateUniqueId,
    translationKey: 'legalEntity.licenceDetails.weapon.state.column',
    isDictionary: true,
    dictionaryType: 'state',
  },
  {
    key: EWeaponPropertyKey.OwnerName,
    translationKey: 'legalEntity.licenceDetails.weapon.owner.column',
    isLegalEntityLink: true,
    legalEntityIdKey: 'ownerUniqueId',
  },
  {
    key: EWeaponPropertyKey.KeeperName,
    translationKey: 'legalEntity.licenceDetails.weapon.keeper.column',
    isLegalEntityLink: true,
    legalEntityIdKey: 'keeperUniqueId',
  },
];

const ATTACHMENT_PROPERTY_CONFIGS = [
  {
    key: EAttachmentPropertyKey.Filename,
    translationKey: 'legalEntity.licenceDetails.attachments.filename.column',
  },
  {
    key: EAttachmentPropertyKey.DocumentTypeUniqueId,
    translationKey: 'legalEntity.licenceDetails.attachments.type.column',
    isDictionary: true,
    dictionaryType: 'documentType',
  },
  {
    key: EAttachmentPropertyKey.Comment,
    translationKey: 'legalEntity.licenceDetails.attachments.comment.column',
  },
];

const SAFE_STORAGE_PROPERTY_CONFIGS = [
  {
    key: ESafeStoragePropertyKey.Address,
    translationKey: 'legalEntity.licenceDetails.safeStorage.address.column',
  },
];

export const compareLicenceOwner = (previous: any, post: any): IComparisonRow[] =>
  compareFields(previous, post, FIELD_CONFIGS.licenceOwner);

export const compareLicenceConditions = (previous: any, post: any): IComparisonRow[] =>
  compareFields(previous, post, FIELD_CONFIGS.licenceConditions);

export const compareBasicStorage = (previous: any, post: any): IComparisonRow[] =>
  compareFields(previous, post, FIELD_CONFIGS.safeStorage);

export const compareAdditionalInformation = (previous: any, post: any): IComparisonRow[] =>
  compareFields(previous, post, FIELD_CONFIGS.additionalInformation);

export const compareGeneralInfo = (previous: any, post: any): IComparisonRow[] =>
  compareFields(previous, post, FIELD_CONFIGS.generalInformation);

export const compareWeapon = (previous: any, post: any): IComparisonSection[] => {
  const sections: IComparisonSection[] = [];

  const prevWeapons = previous?.firearms || [];
  const postWeapons = post?.firearms || [];

  const allWeaponIds = new Set<string>();
  prevWeapons.forEach((w: any) => w.firearmUniqueId && allWeaponIds.add(w.firearmUniqueId));
  postWeapons.forEach((w: any) => w.firearmUniqueId && allWeaponIds.add(w.firearmUniqueId));

  let weaponCounterPrev = 0;
  let weaponCounterPost = 0;
  let weaponCounter = 0;

  Array.from(allWeaponIds).forEach((weaponId) => {
    const prevWeapon = prevWeapons.find((w: any) => w.firearmUniqueId === weaponId);
    const postWeapon = postWeapons.find((w: any) => w.firearmUniqueId === weaponId);

    const hasChanges = isDifferent(prevWeapon, postWeapon);

    if (!hasChanges) return;

    const rows: IComparisonRow[] = [];
    const isWeaponAdded = !prevWeapon && postWeapon;
    const isWeaponRemoved = prevWeapon && !postWeapon;

    if (isWeaponAdded && !isWeaponRemoved) {
      weaponCounterPost += 1;
    } else if (!isWeaponAdded && isWeaponRemoved) {
      weaponCounterPrev += 1;
    } else if (!isWeaponAdded && !isWeaponRemoved) {
      weaponCounterPost += 1;
      weaponCounterPrev += 1;
    }

    WEAPON_PROPERTY_CONFIGS.forEach((prop) => {
      let beforeValue = prevWeapon;
      let afterValue = postWeapon;

      if (prop.key.includes('.')) {
        const keys = prop.key.split('.');
        keys.forEach((key) => {
          beforeValue = beforeValue?.[key];
          afterValue = afterValue?.[key];
        });
      } else {
        beforeValue = beforeValue?.[prop.key];
        afterValue = afterValue?.[prop.key];
      }

      rows.push({
        beforeValue,
        afterValue,
        translationKey: prop.translationKey,
        isDictionary: prop.isDictionary,
        dictionaryType: prop.dictionaryType,
        isLegalEntityLink: prop.isLegalEntityLink,
        legalEntityIdKey: prop.legalEntityIdKey,
        isFirearmLink: prop.isFirearmLink,
        firearmIdKey: prop.firearmIdKey,
        weaponContext: {
          before: prevWeapon,
          after: postWeapon,
        },
      });
    });

    if (rows.length > 0) {
      const weaponIdentifier = postWeapon?.fireCycleId || prevWeapon?.fireCycleId;

      sections.push({
        categoryName: `${EComparisonCategory.Weapon} - ${weaponIdentifier}`,
        categoryTranslationKey: 'legalEntity.licenceDetails.weapon.header',
        rows,
        weaponCategory: postWeapon?.categoryUniqueId || prevWeapon?.categoryUniqueId,
        additionalRow: {
          before: weaponCounterPrev,
          after: weaponCounterPost,
          name: 'legalEntity.licenceDetails.linkedWeapons.header',
        },
        weaponIndex: weaponCounter,
        isNew: isWeaponAdded,
        isRemoved: isWeaponRemoved,
      });

      weaponCounter++;
    }
  });

  return sections;
};

export const compareAttachments = (previous: any, post: any): IComparisonSection[] => {
  const sections: IComparisonSection[] = [];

  const prevAttachments = previous?.attachments || [];
  const postAttachments = post?.attachments || [];

  const allAttachmentIds = new Set<string>();
  prevAttachments.forEach((w: any) => w.uniqueId && allAttachmentIds.add(w.uniqueId));
  postAttachments.forEach(
    (w: any) => w.uniqueId && !w.copyOfUniqueId && allAttachmentIds.add(w.uniqueId),
  );
  let weaponCounter = 0;

  Array.from(allAttachmentIds).forEach((id) => {
    const prevAttachment = prevAttachments.find((w: any) => w.uniqueId === id);
    let postAttachment = postAttachments.find((w: any) => w.uniqueId === id);

    if (!postAttachment) {
      postAttachment = postAttachments.find(
        (w: any) => w.copyOfUniqueId === prevAttachment.uniqueId,
      );
      if (postAttachment) {
        postAttachment.uniqueId = id;
      }
    }

    const hasChanges = isDifferent(prevAttachment, postAttachment);

    if (!hasChanges) return;

    const rows: IComparisonRow[] = [];

    ATTACHMENT_PROPERTY_CONFIGS.forEach((prop) => {
      let beforeValue = prevAttachment;
      let afterValue = postAttachment;

      if (prop.key.includes('.')) {
        const keys = prop.key.split('.');
        keys.forEach((key) => {
          beforeValue = beforeValue?.[key];
          afterValue = afterValue?.[key];
        });
      } else {
        beforeValue = beforeValue?.[prop.key];
        afterValue = afterValue?.[prop.key];
      }

      rows.push({
        beforeValue,
        afterValue,
        translationKey: prop.translationKey,
        isWeaponList: true,
        weaponPropertyKey: prop.key,
        isDictionary: prop.isDictionary,
        dictionaryType: prop.dictionaryType,
      });
    });
    if (rows.length > 0) {
      sections.push({
        categoryName: EComparisonCategory.Attachments,
        categoryTranslationKey: 'legalEntity.licenceDetails.attachments.header',
        rows,
        weaponIndex: weaponCounter,
      });

      weaponCounter++;
    }
  });

  return sections;
};

export const compareDetailedSafeStorages = (previous: any, post: any): IComparisonRow[] => {
  const prevSafeStorages = previous?.conditions.safeStorages || [];
  const postSafeStorages = post?.conditions.safeStorages || [];

  const allSafeStoragesAddressUniqueIds = new Set<string>();
  prevSafeStorages.forEach((s: any) => allSafeStoragesAddressUniqueIds.add(s.addressUniqueId));
  postSafeStorages.forEach((s: any) => allSafeStoragesAddressUniqueIds.add(s.addressUniqueId));

  const rows: IComparisonRow[] = [];

  Array.from(allSafeStoragesAddressUniqueIds).forEach((addressUniqueId, index) => {
    const prevSafeStorage = prevSafeStorages.find(
      (s: any) => s.addressUniqueId === addressUniqueId,
    );
    const postSafeStorage = postSafeStorages.find(
      (s: any) => s.addressUniqueId === addressUniqueId,
    );

    const hasChanges = isDifferent(prevSafeStorage, postSafeStorage);

    if (!hasChanges) return;

    SAFE_STORAGE_PROPERTY_CONFIGS.forEach((prop) => {
      const beforeValue = prevSafeStorage;
      const afterValue = postSafeStorage;

      rows.push({
        beforeValue,
        afterValue,
        translationKey: prop.translationKey,
        weaponPropertyKey: prop.key,
        isDictionary: false,
        isAddress: true,
        titleSecondPart: ` - ${index + 1}`,
      });
    });
  });

  return rows;
};

interface IComparisonSectionsConfig {
  hideOwner?: boolean;
  hideConditions?: boolean;
  hideSafeStorage?: boolean;
  hideAttachments?: boolean;
  hideAdditionalInfo?: boolean;
  hideGeneralInfo?: boolean;
  hideWeaponInfo?: boolean;
}

const getComparisonSectionsConfig = (historyStatus: string): IComparisonSectionsConfig => {
  if (historyStatus === ELicenceHistoryActions.UPDATE_PAYMENT_STATUS)
    return {
      hideOwner: true,
      hideConditions: true,
      hideAttachments: true,
      hideSafeStorage: true,
      hideAdditionalInfo: true,
    };
  return { hideGeneralInfo: true };
};

export const generateComparisonSections = (
  previous: any,
  post: any,
  historyStatus: string,
): IComparisonSection[] => {
  const sections: IComparisonSection[] = [];
  const config = getComparisonSectionsConfig(historyStatus);

  if (!config?.hideOwner) {
    const ownerRows = compareLicenceOwner(previous, post);
    sections.push({
      categoryName: EComparisonCategory.LicenceOwner,
      categoryTranslationKey: 'legalEntity.licenceDetails.owner.header',
      rows: ownerRows,
    });
  }

  if (!config?.hideConditions) {
    const conditionsRows = compareLicenceConditions(previous, post);
    sections.push({
      categoryName: EComparisonCategory.LicenceConditions,
      categoryTranslationKey: 'legalEntity.licenceDetails.conditions.header',
      rows: conditionsRows,
    });
  }

  if (!config?.hideSafeStorage) {
    const safeStorageBasicRows = compareBasicStorage(previous, post);
    const safeStorageDetailedRows = compareDetailedSafeStorages(previous, post);

    sections.push({
      categoryName: EComparisonCategory.SafeStorage,
      categoryTranslationKey: 'legalEntity.licenceDetails.safeStorage.header',
      rows: [...safeStorageBasicRows, ...safeStorageDetailedRows],
    });
  }

  if (!config?.hideWeaponInfo) {
    const weaponSections = compareWeapon(previous, post);

    if (weaponSections.length) {
      sections.push(...weaponSections);
    } else {
      sections.push({
        categoryName: EComparisonCategory.Weapon,
        categoryTranslationKey: 'legalEntity.licenceDetails.weapon.header',
        rows: [],
      });
    }
  }

  if (!config?.hideAttachments) {
    const attachmentsSections = compareAttachments(previous, post);
    if (attachmentsSections.length) {
      sections.push(...attachmentsSections);
    } else {
      sections.push({
        categoryName: EComparisonCategory.Attachments,
        categoryTranslationKey: 'legalEntity.licenceDetails.attachments.header',
        rows: [],
      });
    }
  }

  if (!config?.hideAdditionalInfo) {
    const additionalInfoRows = compareAdditionalInformation(previous, post);
    sections.push({
      categoryName: EComparisonCategory.AdditionalInformation,
      categoryTranslationKey: 'legalEntity.licenceDetails.additionalInfo.header',
      rows: additionalInfoRows,
    });
  }

  if (!config?.hideGeneralInfo) {
    const generalInfoRows = compareGeneralInfo(previous, post);
    sections.push({
      categoryName: EComparisonCategory.GeneralInfo,
      categoryTranslationKey: 'legalEntity.licenceDetails.generalInformation.header',
      rows: generalInfoRows,
    });
  }
  return sections;
};
